import React from 'react';
import BodyMapSVG from './BodyMapSVG';

/**
 * MuscleRecoveryMap — single source of truth for the muscle recovery body
 * silhouette. Used by both the chat `BodyMapCard` and the main Recovery page.
 *
 * Behavior the user asked for: when there's NO recovery data (e.g. brand-new
 * user / no recent workouts), the body fills FULL GREEN at 99% (so every
 * muscle reads "fully recovered"). Previously the main Recovery page just
 * rendered the raw muscleData map → only worked muscles got coloured, the
 * rest stayed as grey outlines and the body looked half-empty.
 *
 * Anything below 100 is treated as "have data" — 100 means "no info" inside
 * BodyMapSVG (intentional dim state, kept for backwards-compat).
 *
 * Props:
 *   - muscleData: { [muscle_group]: { recovery_pct } } from /api/recovery/body-map
 *   - size: 'sm' | 'md' | 'lg'
 *   - showBack, showLabels, interactive — passed through
 */

export const ALL_FRESH = {
  chest:      { recovery_pct: 99 },
  shoulders:  { recovery_pct: 99 },
  upper_back: { recovery_pct: 99 },
  biceps:     { recovery_pct: 99 },
  triceps:    { recovery_pct: 99 },
  core:       { recovery_pct: 99 },
  lower_back: { recovery_pct: 99 },
  quads:      { recovery_pct: 99 },
  hamstrings: { recovery_pct: 99 },
  glutes:     { recovery_pct: 99 },
  calves:     { recovery_pct: 99 },
};

const MuscleRecoveryMap = ({
  muscleData,
  size = 'md',
  showBack = true,
  showLabels = false,
  interactive = false,
  onMuscleClick,
}) => {
  // "Fresh" can arrive two ways:
  //   (a) empty/null muscleData (chat card path — backend returns {} when no recovery_snapshot exists)
  //   (b) every muscle at recovery_pct >= 100 (recovery page path — /recovery/body-map endpoint
  //       defaults untrained muscles to 100). BodyMapSVG treats `100` as "no info → grey",
  //       so without this branch the page would show a grey silhouette while the chat
  //       (path a) shows green. We normalise both paths here so the visual is identical.
  const entries = Object.entries(muscleData || {});
  const hasData = entries.length > 0;
  const allUntrained = hasData && entries.every(([, v]) => (v?.recovery_pct ?? 100) >= 100);
  const dataset = (!hasData || allUntrained) ? ALL_FRESH : muscleData;
  return (
    <BodyMapSVG
      muscleData={dataset}
      highlightMode="recovery"
      size={size}
      showBack={showBack}
      showLabels={showLabels}
      interactive={interactive}
      onMuscleClick={onMuscleClick}
    />
  );
};

export default MuscleRecoveryMap;
